import { FC, useMemo } from 'react'
import { useFieldArray, useFormContext } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { Box, Button, SxProps, Theme } from '@mui/material'

import { useCommandSources, useCommandTypeOptions } from '@hooks'
import { TYPES_SOURCE_OPTIONS } from '@constants'
import { ENTITY_COMMAND_TYPE } from '@types'
import { Variable, FieldInfo } from '@microservices/wiskey-react-components'

import { CommandFieldsItem } from './components/CommandFieldsItem'

export type CommandPickerControllerProps = {
  objectCode: string
  name?: string
  typeName?: string
  commandName?: string
  isTypeHidden?: boolean
  isSingle?: boolean
  isAnotherObjectWithRequestData?: boolean
  canChangeRefObject?: boolean
  containerSx?: SxProps<Theme>
  commandTypes?: ENTITY_COMMAND_TYPE[]
  availableRefObjectCodes?: string[]
  isSelectedCommandOptionDisabled?: boolean
  scriptVariables?: Variable[]
  scriptFieldsInfo?: FieldInfo[]
}

export const CommandPickerController: FC<CommandPickerControllerProps> = ({
  objectCode,
  name = 'commands',
  typeName = 'type',
  commandName = 'code',
  commandTypes,
  isTypeHidden = false,
  isSingle = false,
  isAnotherObjectWithRequestData = false,
  canChangeRefObject = false,
  containerSx,
  availableRefObjectCodes = [],
  isSelectedCommandOptionDisabled = true,
  scriptVariables,
  scriptFieldsInfo,
}) => {
  const { t } = useTranslation()

  const { control } = useFormContext()
  const {
    fields: commandFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name,
  })

  const { objectsOptions, refObjectOptions, loadingObjects, systemsOptions, loadingSystems } =
    useCommandSources(isAnotherObjectWithRequestData, availableRefObjectCodes)

  const objectTitle = useMemo(() => {
    return objectsOptions?.length && objectCode
      ? objectsOptions.find(objOpt => objOpt.id === objectCode)?.label ?? ''
      : ''
  }, [objectsOptions, objectCode])

  const commandTypeOptions = useCommandTypeOptions(commandTypes)

  const disableInputs = !objectCode
  const disableAddButton = useMemo(
    () => Boolean(commandFields.length >= commandTypeOptions.length),
    [commandTypeOptions, commandFields]
  )

  const handleAddCommand = () => {
    append({
      code: null,
      ...(!isTypeHidden && {
        type: null,
      }),
      ...(isAnotherObjectWithRequestData && {
        sourceCommand: null,
        objectTitle: null,
        name: null,
        sourceType: {
          id: TYPES_SOURCE_OPTIONS.OBJECT,
          label: TYPES_SOURCE_OPTIONS.OBJECT,
        },
        parameters: [],
        body: [],
        httpMethod: null,
      }),
    })
  }

  const handleRemoveCommand = (index: number) => remove(index)

  return (
    <Box mb={1} sx={containerSx}>
      {commandFields.map((field, index) => (
        <CommandFieldsItem
          key={field.id}
          canChangeRefObject={canChangeRefObject}
          commandName={commandName}
          disableInputs={disableInputs}
          field={field}
          index={index}
          isAnotherObjectWithRequestData={isAnotherObjectWithRequestData}
          isSelectedCommandOptionDisabled={isSelectedCommandOptionDisabled}
          isSingle={isSingle}
          isTypeHidden={isTypeHidden}
          loadingObjects={loadingObjects}
          loadingSystems={loadingSystems}
          name={name}
          objectCode={objectCode}
          objectTitle={objectTitle}
          objectsOptions={objectsOptions}
          refObjectOptions={refObjectOptions}
          systemsOptions={systemsOptions}
          typeName={typeName}
          typeOptions={commandTypeOptions}
          scriptVariables={scriptVariables}
          scriptFieldsInfo={scriptFieldsInfo}
          onRemoveItem={!isSingle ? handleRemoveCommand : undefined}
        />
      ))}
      {!isSingle && (
        <Button disabled={disableAddButton} variant='outlined' onClick={handleAddCommand}>
          {t('label.addCommand')}
        </Button>
      )}
    </Box>
  )
}
